<template>
  <div id="data-table" class="m-2.5 flex flex-col h-full bg-white overflow-hidden relative shadow-lg">
    <tx-data-table-toolbar
      :loading="loading" :actions="listActions" :total-count="getTotalCount" :selected-count="selectedRows.length" :show-static-actions="showToolbarStaticActions" :show-generate-data-actions="showGenerateDataActions"
      :show-status-filter="props.showStatusFilter" :filtered-count="sortedFilteredData.length" :status-value="statusFilterValue" :is-paginated="isPaginated" @refresh="doRefresh"
      @column-chooser="doShowColumnChooser()" @filter="doToggleFilter" @export="doExport" @hide-inactive="updateFilterCriteria" @generate="generateData" @save-criteria="saveCriteria"
    />
    <tx-data-table-generate-data v-if="generateDataFields && generateDataFields.length && showGenerateDataValue" class="bg-gray-100 " :columns="generateDataFields" :predefined-criteria="predefinedCriteria" :current-criteria="generateCurrentCriteria" @close="showGenerateDataValue = false" @get-data="getData" @save-criteria="saveCriteria" />
    <!-- TABLE -->
    <div v-if="columns.length" class="relative flex flex-col h-full min-w-full overflow-hidden">
      <!-- Header Row -->
      <div
        ref="headerEl"
        class="sticky top-0 z-10 flex flex-col overflow-x-hidden overflow-y-hidden grow-0" :style="{ minHeight: `${filterVisible ? 131 : 46}px` }"
      >
        <!-- Column Resizing gutter -->
        <div
          v-show="resizingCol" :style="{ left: `${resizingColLeft}px` }"
          class="absolute top-0 bottom-0 w-1 h-full bg-dark-grey z-splitter" @mouseup.stop="doStopResizingCol"
        />
        <div :style="{ minWidth: `${fullWidth}px` }">
          <!-- Column headers -->
          <div
            class="h-[46px] m-h-[46px] font-medium bg-gray-100 border-y border-gray-300 flex flex-row items-center"
            :style="{ minWidth: `${fullWidth}px` }" @mousemove="doResizeCol" @mouseup="doStopResizingCol"
          >
            <!-- Header Checkbox -->
            <div class="sticky left-0 p-3 bg-gray-100 w-9">
              <tx-checkbox
                id="tx-datatable-select-all"
                :model-value="selectAll" @change="doSelectAll"
              />
            </div>
            <!-- Header Cell -->
            <div
              v-for="col in actualVisibleColumns" :key="col.property"
              class="flex flex-row items-center justify-start py-2 pl-2 overflow-hidden text-sm font-medium text-left align-middle bg-gray-100 cursor-pointer"
              :style="{ width: `${col.width}px` }" @mouseup="(payload) => doSort(col, payload)"
            >
              <div
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                :title="indexedColumns.get(col.property)?.title"
              >
                {{ indexedColumns.get(col.property)?.title }}
              </div>
              <!-- Header Sort Icon -->
              <div v-if="!utils.isDefined(indexedColumns.get(col.property)?.allowFilterAndSort)" class="pl-1">
                <font-awesome-icon v-if="!sortCriteria.has(col.property)" icon="fa-light fa-sort" class="text-disabled" />
                <font-awesome-icon v-else-if="sortCriteria.get(col.property)" icon="fa-light fa-sort-up" />
                <font-awesome-icon v-else icon="fa-light fa-sort-down" />
              </div>
              <!-- Header resize handle -->
              <div class="grow h-4 min-w-[4px]" />
              <div
                class="box-border w-1 h-4 border-gray-300 border-x cursor-ew-resize"
                @mousedown.stop="(e) => doStartResizingCol(col, e)"
              />
            </div>
            <!-- Header Actions -->
            <div
              v-if="itemActions && itemActions.length"
              class="w-14 h-[44px] sticky right-0 bg-gray-100 text-sm font-medium flex flex-row justify-start align-middle items-center p-2 text-center"
            >
              {{ t('general.actions') }}
            </div>
          </div>

          <!-- Filter Row -->
          <tx-data-table-filter
            v-model:filter-criteria="filterCriteria" v-model:visible="filterVisible"
            :visible-columns="actualVisibleColumns" :full-width="fullWidth" :indexed-columns="indexedColumns" :is-paginated="isPaginated"
            @close="resetFilterCriteria(false, true)" @open-advance-filter="openAdvancedFilter" @open-date-range-filter="openDateRangeFilter" @column-filter-changed="onColumnFilterChanged"
          />
        </div>
      </div>
      <!-- Virtual List -->
      <div v-bind="containerProps" style="overflow-y: overlay;" class="relative grow" @scroll="doSyncScroll">
        <div v-bind="wrapperProps" class="min-h-full">
          <!-- Loading Progress Bar -->
          <tx-indeterminate-progress v-if="loading" :style="{ minWidth: `${fullWidth}px`, height: '5px' }" />
          <div v-if="!loading && !list.length" class="p-2">
            <p>{{ t('general.emptyList') }}</p>
          </div>
          <!-- Data Row -->
          <div
            v-for="item in list" :key="item.index"
            class="h-[46px] m-h-[46px] flex flex-row border-b border-gray-200 box-border" :style="{ minWidth: `${fullWidth}px` }"
            :class="[item.data.selected ? 'bg-yellow-50' : 'bg-white', { 'hover:cursor-pointer': clickableRecord }]"
            @click="onRowClick(item.data.data)" @click.right.stop="onRowRightClick($event, item.data.data)"
          >
            <!-- Row check box -->
            <div class="sticky left-0 p-3 my-1 w-9" :class="item.data.selected ? 'bg-yellow-50' : 'bg-white'">
              <tx-checkbox v-model="item.data.selected" @change="triggerSelectionChanged()" />
            </div>

            <!-- Data Cell -->
            <slot name="fieldViewer" :indexed-columns="indexedColumns" :columns="actualVisibleColumns" :record="item.data.data">
              <tx-data-table-field-viewer
                v-for="col in actualVisibleColumns" :key="col.property" :catalog-independent="catalogIndependent" :column-data="item.data.data" class="px-2" :width="`${col.width}px`" :lookup-data="lookupData[col.property]"
                :value="get(item.data.data, col.property, null)" :field="indexedColumns.get(col.property)!"
              />
            </slot>

            <!-- Actions -->
            <div
              v-if="itemActions && itemActions.length"
              class="sticky right-0 flex flex-row items-center justify-start p-1 text-sm text-center align-middle w-14"
              :class="item.data.selected ? 'bg-yellow-50' : 'bg-white'"
            >
              <tx-button
                type="icon" faicon="fa-light fa-ellipsis-vertical"
                @click.stop="(ev) => onItemActionsIconClick(ev, item.data)"
              />
            </div>
          </div>
          <!-- Load More Row -->
          <div
            v-if="isMoreDataLoading && !loading" class="h-[46px] m-h-[46px] flex flex-row border-b border-gray-200 box-border" :style="{ minWidth: `${fullWidth}px` }"
          >
            <div class="w-full text-center py-2">
              <tx-indeterminate-progress v-if="isMoreDataLoading" :style="{ minWidth: `${fullWidth}px`, height: '5px' }" />
              <p>{{ t('general.loadingMore') }}</p>
            </div>
          </div>
        </div>
        <div ref="actionsEl" class="flex flex-col bg-white shadow-card py-3 min-w-[150px]">
          <div
            v-for="(action, index) in itemActionsRef" v-show="action.visible" :key="index" class="flex items-center p-2 max-h-[300px] overflow-x-hidden overflow-y-auto hover:bg-hover"
            :class="action.enabled ? 'cursor-pointer text-default' : 'cursor-not-allowed text-disabled'"
            @click="onActionClick(action)"
          >
            <font-awesome-icon :icon="action.icon" class="px-2" />
            <div class="px-1">
              {{ action.label }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <tx-data-table-column-chooser ref="columnChooserEl" @ok="doUpdateColumns" />
    <tx-data-table-advance-filter ref="advanceFilterEl" :filter-criteria="filterCriteria" :is-paginated="isPaginated" @ok="doAdvanceFilter" />
    <tx-data-table-date-range-filter ref="dateRangeFilterEl" :filter-criteria="filterCriteria" :is-paginated="isPaginated" @ok="doDateRangeFilter" />
    <tx-data-table-export ref="exportEl" :selection-option="selectedRows.length > 0 ? 1 : (Object.keys(filterCriteria).length > 0 && selectedRows.length === 0) ? 2 : 0" :hide-column-selection="hideColumnSelectionFilter" :data-source-types="dataSourceTypes" @ok="doExportData" />
    <tx-data-table-record-details ref="recordDetailsEl" :fields="props.columns" :title-attribute="props.titleAttribute" />
    <!-- Export dialog for exporting requests -->
    <export-dialog ref="refRequestExportDialog" />
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside, refDebounced, useThrottleFn, useVirtualList } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { get, isFunction } from 'lodash-es'
import ExcelJS from 'exceljs'
import TxButton from '../TxButton.vue'
import TxCheckbox from '../TxCheckbox.vue'
import TxIndeterminateProgress from '../TxIndeterminateProgress.vue'
import { useDynamicPopper } from '../../composables/popper'
import TxDataTableFieldViewer from './TxDataTableFieldViewer.vue'
import TxDataTableColumnChooser from './TxDataTableColumnChooser.vue'
import TxDataTableToolbar from './TxDataTableToolbar.vue'
import TxDataTableGenerateData from './TxDataTableGenerateData.vue'
import TxDataTableFilter from './TxDataTableFilter.vue'
import type { IExportForm } from './TxDataTableExport.vue'
import TxDataTableExport from './TxDataTableExport.vue'
import type { ITxDataTableColumn, ITxDataTableGenerateDataColumns, ITxDataTableItemAction, ITxDataTableListAction, ITxDataTableVisibleColumn } from './TxDataTable.types'
import TxDataTableAdvanceFilter from './TxDataTableAdvanceFilter.vue'
import TxDataTableDateRangeFilter from './TxDataTableDateRangeFilter.vue'
import TxDataTableRecordDetails from './TxDataTableRecordDetails.vue'
import { useUserStore } from '@/store/userData'
import type { IFilterCriteria } from '@/models/filterCriteria'
import { FilterCriteria, FilterCriteriaMode } from '@/models/filterCriteria'
import { generateComparer } from '@/services/dataTableFactory'
import utils from '@/services/utils'
import useMasterDataLookup from '@/modules/admin/composables/masterDataLookup'
import useUserProfileSettings from '@/modules/admin/composables/userProfileSettings'
import ExportDialog from '@/modules/export/ExportDialog.vue'
import appConfig from '@/services/appConfig'
import type Article from '@/models/article'
import type MyArticle from '@/models/myArticle'
import { AttributeType } from '@/models/catalogAttribute'
import RequestModel from '@/models/request'
import { getSeasonlessRequests } from '@/api/t1/seasonlessRequests'
import { getChangeLogs } from '@/api/t1/changeLog'

interface IProps {
  columns: ITxDataTableColumn[]
  data: any[]
  visibleColumns?: ITxDataTableVisibleColumn[]
  loading: boolean
  itemActions: ITxDataTableItemAction[]
  listActions: ITxDataTableListAction[]
  clickableRecord?: boolean
  initialDefaultVisibleColumnMaxCount?: number
  sortColumn?: { [attributeSystemName: string]: boolean }
  showToolbarStaticActions?: boolean
  showGenerateDataActions?: boolean
  allowListDetailsOnRightClick?: boolean
  title?: string
  titleAttribute?: string
  showStatusFilter?: boolean
  isCatalogSpecificModel?: boolean // set this value as flase for non catalog related modules
  modelPath?: string
  totalColumnWidth?: number
  loadingMore?: boolean
  totalDataCount?: number
  isPaginated?: boolean
  defaultFilterCriteria?: Record<string, FilterCriteria>
  hideColumnSelectionFilter?: boolean
  generateDataFields?: ITxDataTableGenerateDataColumns[]
  showGenerateData?: boolean
  predefinedCriteria?: any[]
  catalogIndependent?: boolean
  dataSourceTypes?: string[]
  paginationData?: any
  generateCurrentCriteria?: any
}

const props = withDefaults(defineProps<IProps>(), {
  clickableRecord: false,
  showToolbarStaticActions: true,
  showGenerateDataActions: false,
  initialDefaultVisibleColumnMaxCount: 10,
  allowListDetailsOnRightClick: true,
  titleAttribute: 'ArticleNumber',
  showStatusFilter: false,
  isCatalogSpecificModel: true,
  loadingMore: false,
  totalDataCount: 0,
  isPaginated: false,
  hideColumnSelectionFilter: false,
  showGenerateData: false,
  catalogIndependent: false,
})

const emits = defineEmits<{
  (e: 'refresh'): void
  (e: 'selectionChanged', items: any[]): void
  (e: 'rowClick', rowData: any): void
  (e: 'loadMoreData'): void
  (e: 'dataTableFilterChanged', filterCriteria: Record<string, FilterCriteria>): void
  (e: 'sortByChanged', sortCriteria: Record<string, boolean>): void
  (e: 'getData', val: Record<string, any>): void
  (e: 'saveCriteria', val: Record<string, any>, name: string): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const { loadLookupForTable } = useMasterDataLookup()
const { persistListViewLayout } = useUserProfileSettings()

const indexedColumns = computed(() => new Map(props.columns.map(o => [o.property, o])))
const actualVisibleColumns = ref<ITxDataTableVisibleColumn[]>([])
const filterCriteria = ref<Record<string, FilterCriteria>>({})
const debouncedFilterCriteria = refDebounced(filterCriteria, 500)
const sortCriteria = ref(new Map<string, boolean>(Object.entries(props.sortColumn ?? {})))
const columnChooserEl = ref<InstanceType<typeof TxDataTableColumnChooser>>()
const actionsEl = ref<HTMLInputElement | null>(null)
const headerEl = ref<HTMLElement | null>(null)
const itemActionsRef = ref<{ icon: string, label: string, enabled: boolean, visible: boolean, onClick: () => void }[]>()
const actionsPopper = useDynamicPopper(actionsEl, 'bottom-end')
const selectedRows = ref<any[]>([])
const resizingCol = ref<ITxDataTableVisibleColumn>()
let resizingColStartX = 0
let resizingColStartLeft = 0
let resizingColMoved = false
const resizingColLeft = ref(0)
const filterVisible = ref(false)
const advanceFilterEl = ref<InstanceType<typeof TxDataTableAdvanceFilter>>()
const dateRangeFilterEl = ref<InstanceType<typeof TxDataTableDateRangeFilter>>()
const exportEl = ref<InstanceType<typeof TxDataTableExport>>()
const recordDetailsEl = ref<InstanceType<typeof TxDataTableRecordDetails>>()
const lookupData = ref<Record<string, any>>({})
const refRequestExportDialog = ref<InstanceType<typeof ExportDialog>>()
const isKeepingScrollFromDetails = ref(false)

const showGenerateDataValue = ref(props.showGenerateData)
const constants = {
  defaultColumnWidthRatio: 0.175,
}

onClickOutside(actionsEl, () => {
  actionsPopper.hide()
})

const isMoreDataLoading = computed(() => !!props.loadingMore)
const dataWithSelection = computed(() => props.data.map(o => ({ selected: false, data: o })))

const selectAll = computed(() => dataWithSelection.value.length > 0 && dataWithSelection.value.every(o => o.selected))
const getTotalCount = computed(() => props.data.length)

const sortedFilteredData = computed(() => {
  let data = dataWithSelection.value
  if (props.isPaginated) {
    // API already returning filtered & sorted the records
    return data
  }

  if (debouncedFilterCriteria.value) {
    data = data.filter(i => recordMatchesFilter(i.data, debouncedFilterCriteria.value))
  }
  if (sortCriteria.value.size > 0 && indexedColumns.value.size > 0) {
    const orderedSortCriteria = Array.from(sortCriteria.value, ([property, ascending]) => generateComparer(property, ascending, indexedColumns.value.get(property)!.type))
    data.sort((a, b) => compareRecordSortOrder(a.data, b.data, orderedSortCriteria))
  }
  return data
})
const fullWidth = computed(() => {
  // Add margin and padding
  const margin = 16 // Margin value
  const selectBoxWidth = 20 // Width of the select box
  const actionsWidth = props.itemActions && props.itemActions.length ? 56 : 0 // Actions width

  // Calculate full width
  const calculatedWidth = actualVisibleColumns.value.reduce((p, c) => p + c.width, 0) + margin + selectBoxWidth + actionsWidth

  return calculatedWidth
})

const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(sortedFilteredData, { itemHeight: 50 })
const statusFilterValue = computed(() => {
  if (props.showStatusFilter && filterCriteria.value && filterCriteria.value.Status) {
    return filterCriteria.value.Status.statusVal && filterCriteria.value.Status.statusVal === 1 ? 1 : 0
  }
  else {
    return 0
  }
})

const doResizeCol = useThrottleFn((e: MouseEvent) => {
  if (resizingCol.value && e.currentTarget) {
    const movementOnX = e.clientX - resizingColStartX
    const newColLeft = resizingColLeft.value + movementOnX
    const newWidth = resizingCol.value.width + newColLeft - resizingColStartLeft
    if (newWidth < 75) { return }
    resizingColLeft.value += movementOnX
    resizingColStartX = e.clientX
    resizingColMoved = true
  }
}, 1)

onMounted(() => {
  reCalculateActualVisibleColumns()
  window.addEventListener('keydown', handleHotkey)
  if (props.defaultFilterCriteria && Object.keys(props.defaultFilterCriteria).length > 0) {
    filterCriteria.value = cloneFilterCriteria(props.defaultFilterCriteria)
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleHotkey)
})

function handleHotkey(event) {
  if (event.key === '/') {
    doToggleFilter()
    event.preventDefault()
  }
}

watch(() => props.defaultFilterCriteria, (newCriteria) => {
  if (newCriteria && Object.keys(newCriteria).length > 0) {
    filterCriteria.value = cloneFilterCriteria(newCriteria)
  }
})

watch(() => props.columns, async () => {
  if (userStore.activeCatalog) {
    lookupData.value = await loadLookupForTable(props.columns, userStore.activeCatalog)
  }
  reCalculateActualVisibleColumns()
})

watch(
  () => props.loading,
  (newLoading, oldLoading) => {
    if (oldLoading && !newLoading && !props.loadingMore) {
      // Only scroll to top when a full data reload happens, not when loading more data
      scrollTo(0)
    }
  },
)

function doSort(col: ITxDataTableVisibleColumn, payload: MouseEvent) {
  if (resizingCol.value) { return }

  // for filtarable colums allowFilterAndSort will be udefined
  if (utils.isDefined(indexedColumns.value.get(col.property)?.allowFilterAndSort)) { return }

  // clear sort criteria if user did not hold control key while click and either it was sort on multiple colum or sort was on different column
  if (!payload.ctrlKey && (sortCriteria.value.size > 1 || !sortCriteria.value.has(col.property))) {
    sortCriteria.value.clear()
  }

  if (sortCriteria.value.has(col.property)) {
    sortCriteria.value.set(col.property, !sortCriteria.value.get(col.property))
  }
  else {
    sortCriteria.value.set(col.property, true)
  }
  scrollTo(0)

  emits('sortByChanged', Object.fromEntries(sortCriteria.value))
}

function doShowColumnChooser() {
  columnChooserEl.value?.show(props.columns, actualVisibleColumns.value.map(o => indexedColumns.value.get(o.property)))
}

function doUpdateColumns(columns: ITxDataTableColumn[]) {
  // Remove column that are no longer selected
  const newColumns: ITxDataTableVisibleColumn[] = []
  const totalColumnWidth = utils.isDefined(props.totalColumnWidth) ? props.totalColumnWidth : 1200
  const totalAvailableWidth = fullWidth.value >= totalColumnWidth ? fullWidth.value : totalColumnWidth
  columns.forEach((col, index) => {
    const existingCol = actualVisibleColumns.value.find(i => i.property === col.property)
    const actualWidth = totalAvailableWidth * constants.defaultColumnWidthRatio
    newColumns.push(existingCol ? { property: col.property, width: existingCol.width, order: index } : { property: col.property, width: actualWidth, order: index })
  })
  actualVisibleColumns.value = newColumns
  // save selected columns list in profile settings
  if (utils.isDefined(props.modelPath)) {
    // calculate width ratio for each column
    const columnWidthRatio = actualVisibleColumns.value.reduce((acc, col) => {
      acc[col.property] = col.width / totalAvailableWidth
      return acc
    }, {} as Record<string, number>)

    persistListViewLayout(props.isCatalogSpecificModel, props.modelPath, columnWidthRatio, totalAvailableWidth)
  }

  // If the filter panel is open then we need to update the filter criteria
  if (filterVisible.value) {
    resetFilterCriteria(true)
  }
}

function doSelectAll(val: TxBooleanish) {
  const newVal = val === true || val === 'true' || val === 1
  sortedFilteredData.value.forEach(o => o.selected = newVal)
  triggerSelectionChanged()
}

function triggerSelectionChanged() {
  selectedRows.value = dataWithSelection.value.reduce((acc, cur) => {
    cur.selected && acc.push(cur.data)
    return acc
  }, [] as any[])

  emits('selectionChanged', selectedRows.value)
}

function doRefresh() {
  scrollTo(0)
  emits('refresh')
}

function onRowClick(rowData: any) {
  emits('rowClick', rowData)
}

function onRowRightClick(event: MouseEvent, rowData: any) {
  event.preventDefault()
  if (props.allowListDetailsOnRightClick) {
    // show Details dialog
    recordDetailsEl.value?.showDialog(rowData)
  }
}
function updateFilterCriteria(value: boolean) {
  if (!filterCriteria.value.Status) {
    filterCriteria.value.Status = new FilterCriteria({ attribute: 'Status', exclude: false, mode: FilterCriteriaMode.status, statusVal: value ? 1 : -1 })
  }
  else {
    filterCriteria.value.Status.statusVal = value ? 1 : -1
  }
  if (props.modelPath === 'catalogs.seasonlessRequestsTable' && filterCriteria.value) {
    emits('dataTableFilterChanged', filterCriteria.value)
  }
}

function reCalculateActualVisibleColumns() {
  actualVisibleColumns.value = []
  if (props.visibleColumns && props.visibleColumns.length > 0) {
    const totalColumnWidth = utils.isDefined(props.totalColumnWidth) ? props.totalColumnWidth : 1200
    // Calculate the total available width for columns
    const totalAvailableWidth = fullWidth.value >= totalColumnWidth ? fullWidth.value : totalColumnWidth
    // Calculate actual column widths based on the ratio
    props.visibleColumns.forEach((col) => {
      const actualWidth = totalAvailableWidth * col.width // Assuming col.width is a ratio
      actualVisibleColumns.value.push({ ...col, width: actualWidth })
    })
  }
  else {
    const totalVisibleColumns = props.columns.length
    const columnCount = Math.min(props.initialDefaultVisibleColumnMaxCount, totalVisibleColumns)
    const totalAvailableWidth = fullWidth.value
    const columnWidth = Math.max(totalAvailableWidth / columnCount, 75)

    for (let i = 0; i < columnCount; i++) {
      actualVisibleColumns.value.push({ property: props.columns[i].property, order: i, width: columnWidth })
    }
  }
}

function compareRecordSortOrder(first: any, second: any, criteriaFns: ((a: any, b: any) => any)[]) {
  return criteriaFns.reduce((acu, currentComparer) => acu || currentComparer(first, second), 0)
}

function onItemActionsIconClick(ev: Event, item: { data: any }) {
  itemActionsRef.value = props.itemActions.map((o) => {
    const res = { enabled: false, visible: false, icon: o.icon, label: o.label, onClick: () => o.onClick(item.data) }
    if (isFunction(o.enabled)) {
      res.enabled = o.enabled(item.data)
    }
    else {
      res.enabled = o.enabled
    }
    if (isFunction(o.visible)) {
      res.visible = o.visible(item.data)
    }
    else {
      res.visible = o.visible
    }
    return res
  })
  if (ev.currentTarget instanceof HTMLElement) {
    actionsPopper.show(ev.currentTarget)
  }
  else {
    console.warn(ev.target)
  }
}

function onActionClick(action) {
  if (action.enabled) {
    action.onClick()
    actionsPopper.hide()
    itemActionsRef.value = []
  }
}

function doStartResizingCol(col: ITxDataTableVisibleColumn, e: MouseEvent) {
  resizingColStartX = e.clientX
  const bcr = (e.target as HTMLElement).parentElement?.parentElement?.getBoundingClientRect()
  resizingColLeft.value = e.clientX - (bcr?.left || 0)
  resizingColStartLeft = resizingColLeft.value
  resizingCol.value = col
}

function doStopResizingCol(e: MouseEvent) {
  if (resizingColMoved && resizingCol.value) {
    const newWidth = resizingCol.value.width + resizingColLeft.value - resizingColStartLeft
    resizingCol.value.width = Math.max(newWidth, 75)
    e.stopPropagation()
  }
  resizingCol.value = undefined
  resizingColMoved = false
  // Save selected columns list in profile settings
  if (utils.isDefined(props.modelPath)) {
    // Calculate width ratio for each column
    const columnWidthRatio = actualVisibleColumns.value.reduce((acc, col) => {
      acc[col.property] = col.width / fullWidth.value // Replace col.id with your column identifier
      return acc
    }, {} as Record<string, number>)

    // Persist updated layout with ratio and totalColumnWidth
    persistListViewLayout(props.isCatalogSpecificModel, props.modelPath, columnWidthRatio, fullWidth.value, true)
  }
}
function generateData() {
  showGenerateDataValue.value = !showGenerateDataValue.value
}

function getData(data) {
  generateData()
  emits('getData', data)
}

function saveCriteria(data: Record<string, any>, name: string) {
  emits('saveCriteria', data, name)
}

function doToggleFilter() {
  resetFilterCriteria(false, !!filterVisible.value)
  filterVisible.value = !filterVisible.value
}

function cloneFilterCriteria(criteria: Record<string, FilterCriteria>) {
  const result: Record<string, FilterCriteria> = {}
  for (const key in criteria) {
    if (criteria.hasOwnProperty(key)) {
      result[key] = new FilterCriteria({ ...criteria[key] })
    }
  }
  return result
}

function resetFilterCriteria(keepExistingCriteria: boolean, isCloseActions: boolean = false) {
  if (!keepExistingCriteria) {
    filterCriteria.value = props.defaultFilterCriteria && Object.keys(props.defaultFilterCriteria).length > 0
      ? cloneFilterCriteria(props.defaultFilterCriteria)
      : {}
  }
  actualVisibleColumns.value.forEach((itm) => {
    if (!indexedColumns.value.has(itm.property)) { return }
    const col = indexedColumns.value.get(itm.property)!
    if (!keepExistingCriteria || !filterCriteria.value.hasOwnProperty(itm.property)) {
      const mode = col.type === AttributeType.Int && col.filterLookup && col.filterLookup.size > 0 ? FilterCriteriaMode.multiString : FilterCriteria.attributeTypeToFilterMode(col.type, col.filterLookup)
      if (!props.defaultFilterCriteria || !props.defaultFilterCriteria[itm.property]) {
        filterCriteria.value[itm.property] = new FilterCriteria({
          attribute: itm.property,
          exclude: false,
          mode,
        })
      }
    }
  })

  if (keepExistingCriteria) {
    const existingProps = Object.getOwnPropertyNames(filterCriteria.value)
    existingProps.forEach((prop) => {
      if (!actualVisibleColumns.value.find(e => e.property === prop)) {
        delete filterCriteria.value[prop]
      }
    })
  }

  // Emit filter change event currently specific for 'catalogs.seasonlessRequestsTable'
  if (props.modelPath === 'catalogs.seasonlessRequestsTable' && filterCriteria.value && isCloseActions) {
    emits('dataTableFilterChanged', filterCriteria.value)
  }
}

function recordMatchesFilter(data: any, filterCriteria: Record<string, FilterCriteria>) {
  return Object.getOwnPropertyNames(filterCriteria).every((prop) => {
    const criteria = filterCriteria[prop]

    if (!criteria) {
      return true
    }

    if (criteria.filterBlank) {
      // check if the attribute value is blank (null, undefined, or empty string)
      const isBlankValue = data[criteria.attribute] == null || data[criteria.attribute] === ''
      if (!criteria.exclude) {
        // filter all the blanks
        return !!isBlankValue
      }
      else if (criteria.exclude) {
        // if filterBlank and exclude enabled then we need to show the records having proper value and discard the blank values
        return !isBlankValue
      }
    }

    const hasValue = criteria.hasValue()
    if (!hasValue) {
      return true
    }

    return criteria.matchesRecord(data, true, true)
  })
}

function doSyncScroll() {
  headerEl.value?.scrollTo({ left: containerProps.ref.value?.scrollLeft })

  if (isKeepingScrollFromDetails.value) { return }

  const container = containerProps.ref.value
  if (container) {
    const nearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 10
    if (nearBottom && !isMoreDataLoading.value) {
      emits('loadMoreData')
    }
  }
}

function openAdvancedFilter(col: ITxDataTableVisibleColumn) {
  advanceFilterEl.value?.show(col)
}

function openDateRangeFilter(col: ITxDataTableVisibleColumn) {
  dateRangeFilterEl.value?.show(col)
}

function doAdvanceFilter(columnFilterObject: Record<string, { filterValues: string, filterBlank: boolean, excludeMode: boolean }>) {
  Object.keys(columnFilterObject).forEach((property) => {
    const filterObject = columnFilterObject[property]
    const mode = FilterCriteriaMode.multiString
    const filterValues = filterObject.filterValues.split('\n').map(val => val.trim())

    const filterCriteriaItem = new FilterCriteria({
      attribute: property,
      mode,
      multipleVals: filterObject.filterBlank ? undefined : filterValues,
      exclude: filterObject.excludeMode.valueOf(),
      filterBlank: filterObject.filterBlank.valueOf(),
      isAdvancedFilter: true,
    })
    filterCriteria.value[property] = filterCriteriaItem
  })
  // Emit filter change event currently specific for 'catalogs.seasonlessRequestsTable'
  if (props.modelPath === 'catalogs.seasonlessRequestsTable' && filterCriteria.value) {
    emits('dataTableFilterChanged', filterCriteria.value)
  }
  scrollTo(0)
}

function doDateRangeFilter(columnFilterObject: Record<string, { startDate: string, endDate: string, filterBlank: boolean }>) {
  Object.keys(columnFilterObject).forEach((property) => {
    const filterObject = columnFilterObject[property]
    const filterValues = [filterObject.startDate, filterObject.endDate]
    if (filterCriteria.value[property]) {
      filterCriteria.value[property].multipleVals = filterObject.filterBlank ? undefined : filterValues
      filterCriteria.value[property].filterBlank = filterObject.filterBlank.valueOf()
      filterCriteria.value[property].isAdvancedFilter = true
    }
  })
  // Emit filter change event currently specific for 'catalogs.seasonlessRequestsTable'
  if (props.modelPath === 'catalogs.seasonlessRequestsTable' && filterCriteria.value) {
    emits('dataTableFilterChanged', filterCriteria.value)
  }
  scrollTo(0)
}

function onColumnFilterChanged() {
  // Emit filter change event currently specific for 'catalogs.seasonlessRequestsTable'
  if (props.modelPath === 'catalogs.seasonlessRequestsTable' && filterCriteria.value) {
    emits('dataTableFilterChanged', filterCriteria.value)
  }
  else {
    nextTick(() => scrollTo(0))
  }
}

function doExport() {
  exportEl.value?.show()
}

async function doExportData(form: IExportForm) {
  const isRequestsModule = props.modelPath === 'catalogs.requestsTable' || props.modelPath === 'catalogs.seasonlessRequestsTable'

  const columns: ITxDataTableColumn[] = []
  if (form.columnSelection === 'visible') {
    for (const visibleColumn of actualVisibleColumns.value) {
      const column = indexedColumns.value.get(visibleColumn.property)
      if (column) {
        columns.push(column)
      }
    }
  }
  else {
    for (const column of props.columns) {
      columns.push(column)
    }
  }
  let data = form.dataSource === 'all' ? props.data : form.dataSource === 'filtered' ? sortedFilteredData.value : sortedFilteredData.value.filter(column => column.selected)
  if (form.dataSource === 'all' && props.isPaginated && (!props.totalDataCount || props.data.length < props.totalDataCount)) { // we need to fetch whole list from API
    switch (props.modelPath) {
      case 'catalogs.seasonlessRequestsTable':
        {
          const pageSize = 100
          const total = props.totalDataCount
          const totalPages = Math.ceil(total / pageSize)

          const promises: Promise<any>[] = []
          if (userStore.activeCatalog) {
            for (let page = 0; page < totalPages; page++) {
              promises.push(getSeasonlessRequests({}, page, pageSize))
            }

            const results = await Promise.all(promises)
            const requests = results.flatMap(res => res.data)
            const requestModelData = requests.map((al: any) => new RequestModel(al))
            data = await utils.getModifyArticleRequestsTranslatedData(userStore.activeCatalog, userStore.myAttributes!, requestModelData, userStore.sizeScales, userStore.articleStateList, appConfig.DB!, userStore.priceGroups, true, userStore.indexedCatalogDetailsFromAdmin, userStore.userProfile!.Roles)
          }
        }
        break
      case 'changeLog':
        // maxmimum pages and number of pages
        {
          let keepFetching = true
          let page = 0
          const pageSize = props.paginationData.exportPageSize || 1000
          data = []
          if (userStore.userProfile && props.paginationData && props.paginationData.filterData) {
            while (keepFetching) {
              await getChangeLogs(userStore.userProfile.AccountDetails.OrganizationId!, props.paginationData.filterData, page, pageSize)
                .then((response) => {
                  if (response.data.length) {
                    data.push(...utils.translateChangeLogData(response.data, props.columns))
                    page++
                  }
                  if (response.data.length === 0 || response.data.length < pageSize || props.paginationData.exportLimit <= data.length) {
                    keepFetching = false
                  }
                })
                .catch((error) => {
                  console.warn(error)
                  keepFetching = false
                })
            }
          }
        }
        break
      default:
        console.warn(`Export for modelPath "${props.modelPath}" is paginated but has no full-fetch handler.`)
        break
    }
  }
  else {
    if (props.modelPath === 'changeLog') {
      data = utils.translateChangeLogData(data, props.columns)
    }
  }

  // const lookupData = await loadLookupForTable(columns, userStore.activeCatalog)

  if (!isRequestsModule) {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet()

    worksheet.addRow(columns.map(col => col.title?.replace(/&amp;/, '&')))

    const chunkSize = 1000
    for (let i = 0; i < data.length; i += chunkSize) {
      await new Promise<void>(resolve => setTimeout(resolve, 0))
      const chunk = data.slice(i, i + chunkSize)
      const rows: any[] = []

      for (const item of chunk) {
        const rowData: any[] = []
        for (const column of columns) {
          const propertyValue = form.dataSource === 'all' ? get(item, column.property, null) : get(item.data, column.property, null)
          rowData.push(props.catalogIndependent ? propertyValue : utils.getAdminAttributeValue(column, propertyValue, lookupData.value, userStore.activeCatalog!))
        }

        rows.push(rowData)
      }
      if (rows.length) {
        worksheet.addRows(rows)
      }
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', `${props.title || 'Records'}.xlsx`)
    link.click()

    exportEl.value?.close()
  }
  else if (isRequestsModule) {
    let createArticleRequests: any[] = []
    let modifyArticleRequests: any[] = []

    if (form.dataSource === 'all') { // data is flat
      createArticleRequests = data.filter(request => request.IsCreateArticleRequest)
      modifyArticleRequests = data.filter(request => !request.IsCreateArticleRequest)
    }
    else { // actual data is inside data
      createArticleRequests = data
        .map(request => request.data || {})
        .filter(nested => nested.IsCreateArticleRequest)

      modifyArticleRequests = data
        .map(request => request.data || {})
        .filter(nested => !nested.IsCreateArticleRequest)
    }
    const articlesRequests: Article[] = []
    let myArticlesRequests: MyArticle[] = []
    if (createArticleRequests.length) {
      for (const request of createArticleRequests) {
        if (request.Content) {
          const requestArticle = await utils.getRequestArticle(userStore.activeCatalog!, request, appConfig.DB)
          articlesRequests.push(requestArticle)
        }
      }
      myArticlesRequests = await appConfig.DB!.buildMyArticles(articlesRequests, userStore.activeCatalog!, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
    }
    refRequestExportDialog.value?.generateRequestExcel(myArticlesRequests, modifyArticleRequests)
    exportEl.value?.close()
  }
}

function setFilterCriteria(criteria: IFilterCriteria[]) {
  if (criteria.length) {
    filterVisible.value = true
    filterCriteria.value = {}
    criteria.forEach((c) => {
      filterCriteria.value[c.attribute] = new FilterCriteria(c)
    })

    // Emit filter change event currently specific for 'catalogs.seasonlessRequestsTable'
    if (props.modelPath === 'catalogs.seasonlessRequestsTable' && filterCriteria.value) {
      emits('dataTableFilterChanged', filterCriteria.value)
    }
  }
}

defineExpose({
  filterCriteria,
  setFilterCriteria,
  getScrollPosition() {
    if (containerProps.ref.value) {
      return containerProps.ref.value?.scrollTop
    }
    return 0
  },
  scrollToPosition(position: number) {
    isKeepingScrollFromDetails.value = true
    if (containerProps.ref.value) {
      containerProps.ref.value.scrollTop = position
    }
    setTimeout(() => {
      isKeepingScrollFromDetails.value = false
    }, 100)
  },
})
</script>
